"use client"

/**
 * Web Widget Settings Panel
 *
 * Appearance, branding, and token management for web_widget channels.
 */

import { useCallback, useEffect, useRef, useState } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Slider } from "@/components/ui/slider"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { CollapsibleInfo } from "@/components/ui/collapsible-info"
import { KeyRound, Loader2, Save, Sparkles } from "lucide-react"
import { toast } from "sonner"
import { useAuth } from "@/hooks/auth/use-auth"
import type { MessageAgentChannel } from "@/hooks/agents/messaging/use-agents"
import type { WebWidgetConfig } from "@/lib/agents/messaging/types"
import { WIDGET_BUBBLE_OFFSET, WIDGET_BUBBLE_SIZE, WIDGET_DEFAULT_CONFIG } from "@/lib/agents/messaging/channels/web-widget/constants"
import { WidgetPreview, WIDGET_PREVIEW_WIDTH } from "./widget-preview"
import { WidgetAppearanceFields } from "./widget-appearance-fields"
import { AvatarUploadControl } from "./avatar-upload-control"
import { RegenerateTokenButton } from "./regenerate-token-button"

interface WebWidgetSettingsProps {
  channel: MessageAgentChannel
  onRefresh: () => void
  canManage: boolean
}

export function WebWidgetSettings({ channel, onRefresh, canManage }: WebWidgetSettingsProps) {
  const { fetchWithAuth } = useAuth()
  const channelConfig = (channel.config ?? {}) as Partial<WebWidgetConfig>

  const [config, setConfig] = useState<WebWidgetConfig>({
    ...WIDGET_DEFAULT_CONFIG,
    ...channelConfig,
  })
  const [isSaving, setIsSaving] = useState(false)
  const [isRegenerating, setIsRegenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [canCustomizeBranding, setCanCustomizeBranding] = useState(false)
  const promptInfoRef = useRef<HTMLDivElement>(null)

  // Scroll the expanded info block into view inside the right column's own scroll container.
  const handlePromptInfoOpen = useCallback((open: boolean) => {
    if (!open) return
    requestAnimationFrame(() => {
      promptInfoRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" })
    })
  }, [])

  useEffect(() => {
    fetchWithAuth(`/api/agents/messaging/channels/web-widget/config?channelId=${channel.id}`)
      .then(res => res.ok ? res.json() : null)
      .then(data => { if (data) setCanCustomizeBranding(data.canCustomizeBranding) })
      .catch(() => {})
  }, [channel.id, fetchWithAuth])

  const handleSave = async () => {
    setError(null)
    setIsSaving(true)
    try {
      const response = await fetchWithAuth("/api/agents/messaging/channels/web-widget/config", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ channelId: channel.id, config }),
      })

      if (!response.ok) {
        const data = await response.json()
        setError(data.error || "Failed to save settings")
        return
      }

      toast.success("Widget settings saved", {
        description: "Changes may take up to 15 minutes to appear on your website.",
      })
      onRefresh()
    } catch {
      setError("Failed to save settings")
    } finally {
      setIsSaving(false)
    }
  }

  const handleRegenerateToken = async () => {
    setError(null)
    setIsRegenerating(true)
    try {
      const response = await fetchWithAuth("/api/agents/messaging/channels/web-widget/regenerate-token", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ channelId: channel.id }),
      })

      if (!response.ok) {
        const data = await response.json()
        setError(data.error || "Failed to regenerate token")
        return
      }

      toast.success("Token regenerated. Update embed code on your website.")
      onRefresh()
    } catch {
      setError("Failed to regenerate token")
    } finally {
      setIsRegenerating(false)
    }
  }

  const avatarControl = (
    <AvatarUploadControl
      avatarUrl={config.avatarUrl}
      channelId={channel.id}
      uploadPath="/api/agents/messaging/channels/web-widget/avatar"
      canManage={canManage}
      onChange={(url) => {
        setConfig({ ...config, avatarUrl: url })
        if (url) onRefresh()
      }}
      onError={setError}
    />
  )

  return (
    <div className="flex gap-6">
      {/* Left column: settings */}
      <div className="flex-1 min-w-0 space-y-6">
      {error && (
        <Alert className="border-red-200 bg-red-50">
          <AlertDescription className="text-red-800">{error}</AlertDescription>
        </Alert>
      )}

      <WidgetAppearanceFields
        config={config}
        onChange={setConfig}
        canManage={canManage}
        avatarControl={avatarControl}
        canCustomizeBranding={canCustomizeBranding}
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Window Placement</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Label>Position</Label>
          <div className="flex gap-2">
            {(["bottom-left", "bottom-right"] as const).map((pos) => (
              <Button
                key={pos}
                variant={config.position === pos ? "default" : "outline"}
                size="sm"
                onClick={() => setConfig({ ...config, position: pos })}
                disabled={!canManage}
              >
                {pos === "bottom-right" ? "Bottom Right" : "Bottom Left"}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* --- Chat Button --- */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Chat Button</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Style</Label>
              <div className="flex gap-2">
                {(["filled", "outline"] as const).map((style) => (
                  <Button
                    key={style}
                    variant={config.bubbleStyle === style ? "default" : "outline"}
                    size="sm"
                    onClick={() => setConfig({ ...config, bubbleStyle: style })}
                    disabled={!canManage}
                  >
                    {style === "filled" ? "Filled" : "Outline"}
                  </Button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Entrance Animation</Label>
              <div className="flex flex-wrap gap-2">
                {(["none", "bounce", "pulse", "wiggle"] as const).map((anim) => (
                  <Button
                    key={anim}
                    variant={(config.bubbleAnimation ?? "bounce") === anim ? "default" : "outline"}
                    size="sm"
                    onClick={() => setConfig({ ...config, bubbleAnimation: anim })}
                    disabled={!canManage}
                  >
                    {anim === "none" ? "None" : anim.charAt(0).toUpperCase() + anim.slice(1)}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          {config.bubbleAnimation !== "none" && (
            <div className="space-y-2">
              <Label><span>Animation Delay</span> <span className="text-gray-400 font-normal">{config.bubbleAnimationDelay ?? 3}s</span></Label>
              <Slider
                value={[config.bubbleAnimationDelay ?? 3]}
                onValueChange={([v]) => setConfig({ ...config, bubbleAnimationDelay: v })}
                min={0}
                max={10}
                step={1}
                disabled={!canManage}
              />
            </div>
          )}

          <PxSlider
            label="Button Size"
            value={config.bubbleSize ?? WIDGET_BUBBLE_SIZE.default}
            range={WIDGET_BUBBLE_SIZE}
            step={4}
            onChange={(bubbleSize) => setConfig({ ...config, bubbleSize })}
            disabled={!canManage}
          />

          <div className="grid grid-cols-2 gap-4">
            <PxSlider
              label="Offset from Side"
              value={config.bubbleOffsetX ?? WIDGET_BUBBLE_OFFSET.default}
              range={WIDGET_BUBBLE_OFFSET}
              step={5}
              onChange={(bubbleOffsetX) => setConfig({ ...config, bubbleOffsetX })}
              disabled={!canManage}
            />
            <PxSlider
              label="Offset from Bottom"
              value={config.bubbleOffsetY ?? WIDGET_BUBBLE_OFFSET.default}
              range={WIDGET_BUBBLE_OFFSET}
              step={5}
              onChange={(bubbleOffsetY) => setConfig({ ...config, bubbleOffsetY })}
              disabled={!canManage}
            />
          </div>
        </CardContent>
      </Card>

      {/* --- Website --- */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Website</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Label htmlFor="widget-site-url">Website URL</Label>
          <Input
            id="widget-site-url"
            type="url"
            placeholder="https://example.com"
            value={config.siteUrl ?? ""}
            onChange={(e) => setConfig({ ...config, siteUrl: e.target.value })}
            disabled={!canManage}
            className="text-sm"
          />
          <p className="text-xs text-gray-500">
            The page where this widget is installed. Saved on Publish — gives a link back to the live site from the agent card and conversations.
          </p>
        </CardContent>
      </Card>

      {/* --- Publish --- */}
      {canManage && (
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={isSaving} className="bg-blue-600 hover:bg-blue-700">
            {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            <span>Publish Appearance</span>
          </Button>
        </div>
      )}

      {canManage ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <KeyRound className="h-4 w-4" />
              <span>Embed code access</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              The installation code is shown directly on this channel in the
              Connected Channels list.
            </p>
            <RegenerateTokenButton
              onConfirm={handleRegenerateToken}
              loading={isRegenerating}
            />
          </CardContent>
        </Card>
      ) : null}
      </div>

      <div className="hidden lg:flex lg:flex-col lg:items-center sticky top-0 self-start pt-2 pb-4 gap-4 max-h-screen overflow-y-auto">
        <WidgetPreview config={config} />

        <div
          ref={promptInfoRef}
          className="w-full"
          style={{ maxWidth: WIDGET_PREVIEW_WIDTH }}
        >
          <CollapsibleInfo
            title="AI Output Format"
            icon={<Sparkles className="w-4 h-4 text-indigo-500" />}
            titleClassName="text-sm"
            onOpenChange={handlePromptInfoOpen}
          >
            <div className="space-y-3 text-sm text-gray-600">
              <p>
                <span>The agent&apos;s system prompt is</span> <strong>automatically enriched</strong>{" "}
                <span>with Markdown formatting rules whenever a conversation runs on this
                channel — no manual prompt edits needed.</span>
              </p>

              <div>
                <p className="font-medium text-gray-700 mb-1">Supported:</p>
                <ul className="list-disc list-inside space-y-0.5 text-xs">
                  <li>
                    <span>Links</span> <code className="bg-gray-100 px-1 rounded">[text](url)</code>{" "}
                    <span>— only</span> <code className="bg-gray-100 px-1 rounded">http</code><span>,</span>{" "}
                    <code className="bg-gray-100 px-1 rounded">https</code><span>,</span>{" "}
                    <code className="bg-gray-100 px-1 rounded">mailto</code>
                  </li>
                  <li>
                    <strong>bold</strong><span>,</span> <em>italic</em><span>,</span> <s>strikethrough</s>
                  </li>
                  <li>Bullet and numbered lists</li>
                  <li>
                    <span>Inline</span> <code className="bg-gray-100 px-1 rounded">code</code>{" "}
                    <span>and fenced code blocks</span>
                  </li>
                  <li>Blockquotes</li>
                </ul>
              </div>

              <div>
                <p className="font-medium text-gray-700 mb-1">Stripped on render:</p>
                <ul className="list-disc list-inside space-y-0.5 text-xs">
                  <li>
                    <span>Headings (</span><code className="bg-gray-100 px-1 rounded"># H1</code> <span>…</span>{" "}
                    <code className="bg-gray-100 px-1 rounded">###### H6</code><span>) — use</span>{" "}
                    <strong>bold</strong> <span>for emphasis instead</span>
                  </li>
                  <li>
                    <span>Task lists (</span>
                    <code className="bg-gray-100 px-1 rounded">- [ ] item</code><span>) —
                    checkbox elements are not allowed</span>
                  </li>
                  <li>Tables, images, raw HTML</li>
                  <li>
                    <span>Disallowed URL schemes (e.g.</span>{" "}
                    <code className="bg-gray-100 px-1 rounded">javascript:</code><span>,</span>{" "}
                    <code className="bg-gray-100 px-1 rounded">data:</code><span>)</span>
                  </li>
                </ul>
              </div>

              <p className="text-xs text-gray-500 italic">
                Other channels (Instagram, Facebook, Telegram) stay plain text — the
                Markdown hint is scoped to the web widget only.
              </p>
            </div>
          </CollapsibleInfo>
        </div>
      </div>
    </div>
  )
}

function PxSlider({ label, value, range, step, onChange, disabled }: {
  label: string
  value: number
  range: { min: number; max: number; default: number }
  step: number
  onChange: (value: number) => void
  disabled: boolean
}) {
  const suffix = value === range.default ? " (default)" : ""
  return (
    <div className="space-y-2">
      <Label><span>{label}</span> <span className="text-gray-400 font-normal">{`${value}px${suffix}`}</span></Label>
      <Slider
        value={[value]}
        onValueChange={([next]) => onChange(next)}
        min={range.min}
        max={range.max}
        step={step}
        disabled={disabled}
      />
    </div>
  )
}
